import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, Loader, ArrowRight, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, timeout
  const [partnerId, setPartnerId] = useState('');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 30; // 30 * 2s = 60s

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/partner/registrieren');
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams, navigate]);

  const pollPaymentStatus = async (sessionId) => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/checkout-status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        // Get partner_id from response
        if (data.partner_id) {
          setPartnerId(data.partner_id);
        }
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), 2000);
    } catch (error) {
      console.error('Status check failed:', error);
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), 2000);
    }
  };

  const handleContinue = () => {
    if (partnerId) {
      // Redirect to agreement page
      navigate(`/partner/vereinbarung?partner_id=${partnerId}`);
    } else {
      // Fallback to email verification
      navigate('/portal/verify-email-hint');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          {status === 'checking' && (
            <div className="text-center">
              <Loader className="w-16 h-16 text-brand-green mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Zahlung wird bestätigt...
              </h1>
              <p className="text-gray-600">Bitte einen Moment Geduld</p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-white border border-gray-200 rounded-3xl p-12 shadow-lg text-center">
              <div className="w-20 h-20 bg-brand-green/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-brand-green/15">
                <Check className="w-10 h-10 text-brand-green" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                Zugang freigeschaltet!
              </h1>
              
              {partnerId ? (
                <>
                  <p className="text-xl text-gray-600 mb-4">
                    Nur noch ein Schritt: Bitte unterschreiben Sie die Partnervereinbarung.
                  </p>
                  <div className="bg-brand-green/5 border border-brand-green/20 rounded-2xl p-4 mb-8">
                    <div className="flex items-center justify-center gap-3 text-brand-green">
                      <FileText className="w-5 h-5" />
                      <span className="font-medium">Partnervereinbarung mit Provisionsregelung</span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-xl text-gray-600 mb-10">
                  Bitte bestätigen Sie Ihre E-Mail-Adresse.
                </p>
              )}
              
              <Button
                onClick={handleContinue}
                size="lg"
                className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-2xl font-semibold"
                data-testid="continue-btn"
              >
                {partnerId ? 'Zur Vereinbarung' : 'Weiter'}
                <ArrowRight className="w-5 h-5 ml-3" />
              </Button>
            </div>
          )}

          {status === 'timeout' && (
            <div className="bg-white border border-yellow-200 rounded-3xl p-12 shadow-lg text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Prüfung läuft noch
              </h1>
              <p className="text-gray-600 mb-6">
                Wir prüfen noch Ihre Zahlung. Bitte aktualisieren Sie die Seite oder kontaktieren Sie uns.
              </p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="border-2 border-gray-900"
              >
                Status erneut prüfen
              </Button>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;